import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import Header from '@components/shared/Header';
import darkContentCss from '@styles/darkContentSection.module.less';
import classNames from 'classnames';
import { BuildingListing, StoreState } from '@root/types';
import { getCurrentUser, getVisibleTourbookSummaries } from '@store/selectors';
import { filter, uniqBy } from 'lodash';
import ControlledTabs from '@components/shared/Tabs/ControlledTabs';
import actions from '@store/actions/buildingPage';
import css from './AvailableSpaces.module.less';
import AvailableSpacesFullTable from './FullTable';
import AvailableSpacesMobile from './Mobile';

const mapStateToProps = (state: StoreState, { buildingSlug }: Props) => ({
  listings: state.buildings.listingsBySlug[buildingSlug],
  tourbooks: getVisibleTourbookSummaries(state),
  currentUser: getCurrentUser(state),
});

const mapDispatch = {};

const connector = connect(mapStateToProps, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

type Props = {
  buildingSlug: string;
  listingClicked: (listing: BuildingListing, tab: string) => void;
};

export type ListingTab = 'all' | 'direct';

// Note this component filters listings between direct and all lease types
// with the assumption a building won't have more than 20 listings
export const RawAvailableSpaces = ({
  buildingSlug,
  listingClicked,
  listings,
  tourbooks,
  currentUser,
}: Props & ReduxProps) => {
  const { t } = useTranslation('building');
  const dispatch = useDispatch();
  const [filteredListings, setFilteredListings] = useState<BuildingListing[]>([]);
  const [listingTab, setListingTab] = useState<ListingTab>('all');
  const listingLeaseTypes = uniqBy(listings, 'leaseType').map(listing => listing.leaseType);
  const showTabs = listingLeaseTypes.length >= 2 && listingLeaseTypes.includes('direct');
  const tabSelectedForAnalytics = showTabs ? listingTab : '';

  useEffect(() => {
    if (listingTab === 'direct') {
      setFilteredListings(filter(listings, { leaseType: 'direct' }));
    } else {
      setFilteredListings(listings || []);
    }
  }, [listingTab, listings]);

  useEffect(() => {
    if (currentUser && showTabs) {
      setListingTab('direct');
    }
  }, [currentUser, listings]);

  if (!listings || listings.length === 0) return null;

  const isUserLoggedIn = !!currentUser;

  const changeListingTab = (tab: ListingTab) => {
    setListingTab(tab);
    if (tab === 'all') {
      dispatch(actions.listingTableAllListingsTabClicked());
    } else if (tab === 'direct') {
      dispatch(actions.listingTableDirectListingsTabClicked());
    }
  };

  return (
    <div
      data-testid="available-spaces"
      className={classNames(css.sectionContainer, darkContentCss.darkContentSection)}
    >
      <Header>{t('availableSpaces.sectionHeader')}</Header>
      {isUserLoggedIn && showTabs && (
        <ControlledTabs
          aligned="left"
          currentTab={listingTab}
          tabs={[
            { value: 'direct', label: t('availableSpaces.tabs.directListings') },
            { value: 'all', label: t('availableSpaces.tabs.allListings') },
          ]}
          changeTab={tab => changeListingTab(tab as ListingTab)}
        />
      )}
      <AvailableSpacesFullTable
        listingClicked={listingClicked}
        listings={filteredListings}
        buildingSlug={buildingSlug}
        tourbooks={tourbooks}
        isUserLoggedIn={isUserLoggedIn}
        tabSelectedForAnalytics={tabSelectedForAnalytics}
      />
      <AvailableSpacesMobile
        listingClicked={listingClicked}
        listings={filteredListings}
        buildingSlug={buildingSlug}
        tourbooks={tourbooks}
        isUserLoggedIn={isUserLoggedIn}
        tabSelectedForAnalytics={tabSelectedForAnalytics}
      />
    </div>
  );
};

export default connector(RawAvailableSpaces);
